import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { formatAmount } from "@/utils/formatAmount";
import { Award, Ban, Calendar, Gavel, MapPin, Search, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { PageLoader } from "@/components/ui/Spinner";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  type PublicProceeding,
  useMyProceedings,
} from "@/features/rfq/api/proceedings";
import { offererDeadlineTone } from "@/features/rfq/components/ProceedingOffererCard";

/**
 * „Moje postępowania ofertowe" oczami podwykonawcy.
 *
 * Dwie sekcje: AKTUALNE (opublikowane, w których firma bierze udział albo może
 * jeszcze złożyć ofertę) i ARCHIWALNE (rozstrzygnięte/anulowane, w których firma
 * startowała) — przy archiwalnych widać kwotę oferty, kiedy złożona i wynik.
 * Zawężenie robi backend na podstawie konta, więc nie da się podejrzeć cudzych.
 */

export function MyProceedingsPage() {
  usePageTitle("Moje postępowania ofertowe");
  const { data, isLoading } = useMyProceedings();
  const [search, setSearch] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);

  const q = search.trim().toLowerCase();
  const bySearch = useMemo(
    () => (p: PublicProceeding) =>
      !q || `${p.number} ${p.title}`.toLowerCase().includes(q),
    [q],
  );

  if (isLoading || !data) return <PageLoader />;

  const empty = data.current.length === 0 && data.archived.length === 0;
  const current = data.current
    .filter(bySearch)
    .filter((p) => !onlyMine || p.my_offer?.status === "submitted");
  const archived = data.archived.filter(bySearch);

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Moje postępowania ofertowe
        </h1>
        <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />
        <p className="mt-3 max-w-2xl text-sm text-[var(--color-text-secondary)]">
          Zapytania GK ELEKTIM, do których mają Państwo dostęp. Zakres dostępu
          wynika z przyznanych kategorii prac i obszaru działania.
        </p>
      </div>

      {empty ? (
        <div className="border border-dashed border-[var(--color-border)] px-6 py-12 text-center">
          <Gavel
            size={28}
            className="mx-auto mb-3 text-[var(--color-text-muted)]"
          />
          <p className="text-sm text-[var(--color-text-secondary)]">
            Obecnie nie ma postępowań w Państwa zakresie.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="relative min-w-[16rem] flex-1">
              <Search
                size={14}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Szukaj po numerze lub tytule…"
                className="w-full border border-[var(--color-border)] bg-transparent py-1.5 pl-8 pr-3 text-sm text-[var(--color-text-primary)]"
              />
            </div>
            <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-[var(--color-text-secondary)]">
              <input
                type="checkbox"
                checked={onlyMine}
                onChange={(e) => setOnlyMine(e.target.checked)}
              />
              Tylko w których startuję
            </label>
          </div>

          <div className="space-y-8">
            <Section title="Aktualne" count={current.length}>
              {current.length === 0 ? (
                <EmptyNote>
                  {onlyMine
                    ? "Nie startujesz jeszcze w żadnym trwającym postępowaniu."
                    : "Brak trwających postępowań w Państwa zakresie."}
                </EmptyNote>
              ) : (
                current.map((p) => <CurrentCard key={p.id} proceeding={p} />)
              )}
            </Section>

            {data.archived.length > 0 && (
              <Section title="Archiwalne" count={archived.length}>
                {archived.length === 0 ? (
                  <EmptyNote>Nic nie pasuje do wyszukiwania.</EmptyNote>
                ) : (
                  archived.map((p) => <ArchivedCard key={p.id} proceeding={p} />)
                )}
              </Section>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-2 border-l-4 border-[var(--color-primary)] pl-3 text-lg font-semibold text-[var(--color-text-primary)]">
        {title} ({count})
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="border border-dashed border-[var(--color-border)] px-4 py-6 text-center text-sm text-[var(--color-text-muted)]">
      {children}
    </p>
  );
}

function CardShell({
  proceeding,
  children,
  right,
}: {
  proceeding: PublicProceeding;
  children: React.ReactNode;
  right: React.ReactNode;
}) {
  return (
    <Link
      to={`/contractor/proceedings/${proceeding.id}`}
      className="block border border-[var(--color-border)] border-l-4 border-l-[var(--color-primary)] bg-white p-4 hover:bg-[var(--color-bg-elevated)] dark:bg-gray-800"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
            {proceeding.title}
          </h3>
          <p className="font-mono text-xs text-[var(--color-text-muted)]">
            {proceeding.number}
          </p>
        </div>
        {right}
      </div>
      {children}
    </Link>
  );
}

function CurrentCard({ proceeding }: { proceeding: PublicProceeding }) {
  const tone = offererDeadlineTone(
    proceeding.submission_deadline,
    proceeding.is_open_for_offers,
  );
  const started = proceeding.my_offer?.status === "submitted";
  return (
    <CardShell
      proceeding={proceeding}
      right={
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge color={tone.color}>{tone.label}</Badge>
          {proceeding.runoff?.is_open && <Badge color="orange">Dopytanie</Badge>}
          {proceeding.is_open_for_offers && (
            <Badge color={started ? "green" : "blue"}>
              {started ? "Startujesz" : "Możesz złożyć"}
            </Badge>
          )}
        </div>
      }
    >
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[var(--color-text-secondary)]">
        <span className="flex items-center gap-1">
          <Calendar size={12} /> Oferty do:{" "}
          {new Date(proceeding.submission_deadline).toLocaleString("pl", {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </span>
        {proceeding.voivodeship_label && (
          <span className="flex items-center gap-1">
            <MapPin size={12} /> {proceeding.voivodeship_label}
            {proceeding.county_labels.length > 0 && ` (${proceeding.county_labels.join(", ")})`}
          </span>
        )}
        {proceeding.max_price_net && (
          <span className="font-medium text-[var(--color-text-primary)]">
            Cena maks.: {formatAmount(proceeding.max_price_net)} zł netto
          </span>
        )}
      </div>
      {/* Trwająca dogrywka to termin, którego nie widać w kolumnie „Oferty do" —
          firma musi go zobaczyć już na liście, a nie dopiero w mailu. */}
      {proceeding.runoff?.is_open && proceeding.runoff.participating && (
        <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-500">
          Dopytanie do oferty — termin{" "}
          {new Date(proceeding.runoff.deadline).toLocaleString("pl", {
            dateStyle: "short",
            timeStyle: "short",
          })}{" "}
          , możesz obniżyć swoją ofertę.
        </p>
      )}
      {started ? (
        <p className="mt-2 text-xs font-medium text-green-700 dark:text-green-400">
          Złożono ofertę: {formatAmount(proceeding.my_offer!.amount_net)} zł netto
        </p>
      ) : (
        proceeding.is_open_for_offers && (
          <p className="mt-2 text-xs text-[var(--color-text-muted)]">
            Nie złożyłeś jeszcze oferty — otwórz, aby wystartować.
          </p>
        )
      )}
    </CardShell>
  );
}

const OUTCOME_BADGE = {
  won: { color: "green" as const, icon: Award, label: "Oferta wybrana" },
  lost: { color: "gray" as const, icon: XCircle, label: "Nie wybrano" },
  cancelled: { color: "yellow" as const, icon: Ban, label: "Anulowane" },
  awarded: { color: "gray" as const, icon: Award, label: "Rozstrzygnięte" },
};

function ArchivedCard({ proceeding }: { proceeding: PublicProceeding }) {
  const meta = proceeding.outcome ? OUTCOME_BADGE[proceeding.outcome] : null;
  const Icon = meta?.icon;
  return (
    <CardShell
      proceeding={proceeding}
      right={
        meta ? (
          <Badge color={meta.color}>
            {Icon && <Icon size={12} className="mr-1 inline" />}
            {meta.label}
          </Badge>
        ) : null
      }
    >
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[var(--color-text-secondary)]">
        {proceeding.my_offer && (
          <span className="font-medium text-[var(--color-text-primary)]">
            Twoja oferta: {formatAmount(proceeding.my_offer.amount_net)} zł
            netto
          </span>
        )}
        {proceeding.my_offer?.submitted_at && (
          <span className="flex items-center gap-1">
            <Calendar size={12} /> Złożono:{" "}
            {new Date(proceeding.my_offer.submitted_at).toLocaleString("pl", {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </span>
        )}
      </div>
    </CardShell>
  );
}
